import { useMemo } from 'react';

import classNames from 'classnames';

interface UseRichTextClassesProps {
  isDisabled?: boolean;
  isValid?: boolean;
  isFocused: boolean;
  hasValue: boolean;
  className?: string;
}

/**
 * Generates Tailwind classes matching the Input component from erg-core-components.
 * Mirrors useInputClasses.ts styling for visual consistency.
 */
export const useRichTextClasses = ({
  isDisabled,
  isValid,
  isFocused,
  hasValue,
  className,
}: UseRichTextClassesProps) => {
  const containerClasses = useMemo(() => {
    return classNames(
      'relative flex flex-col gap-1 w-full box-border transition-all duration-100 ease-linear',
      { 'opacity-50': isDisabled },
      className,
    );
  }, [isDisabled, className]);

  const editorWrapperClasses = useMemo(() => {
    const stateConfig = {
      invalid: {
        focused: 'bg-red-50 dark:bg-red-950 border-red-400 dark:border-red-600 ring-4 ring-red-200 dark:ring-red-800',
        default: 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 hover:border-red-500',
      },
      valid: {
        focused: 'bg-green-50 dark:bg-green-950 border-green-400 dark:border-green-600 ring-4 ring-green-200 dark:ring-green-800',
        default: 'bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 hover:border-green-500',
      },
      neutral: {
        focused: 'bg-gray-50 dark:bg-gray-900 border-gray-600 dark:border-gray-400 ring-4 ring-gray-300 dark:ring-gray-700',
        default: 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-gray-400',
      },
    };

    const getStateType = () => {
      if (isValid === false) return 'invalid';
      if (isValid === true) return 'valid';
      return 'neutral';
    };
    const stateType = getStateType();
    const stateClasses = stateConfig[stateType][isFocused ? 'focused' : 'default'];

    return classNames(
      'w-full border rounded transition-all duration-100 ease-linear',
      stateClasses,
      { 'pointer-events-none': isDisabled },
    );
  }, [isValid, isFocused, isDisabled]);

  const textareaClasses = useMemo(() => {
    return classNames(
      // Base sizing - matches core-components Input multiline
      'w-full h-auto p-3 resize-none',
      'bg-transparent border-none outline-none',
      'text-gray-800 dark:text-gray-200 paragraph-2',
      // Scrolling behavior
      'overflow-y-auto align-bottom',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'disabled:text-gray-500 dark:disabled:text-gray-400',
      // List styles - Tailwind resets these by default
      '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2',
      '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2',
      '[&_li]:ml-2',
      // Nested lists (e.g. ol inside ul) - reduce top margin for tighter nesting
      '[&_li_ol]:mt-1 [&_li_ul]:mt-1',
      // Link styles for pasted content
      '[&_a]:text-blue-500 [&_a]:dark:text-blue-400 [&_a]:hover:text-blue-600 [&_a]:hover:underline',
      // Paragraph spacing for pasted content
      '[&_p]:my-2',
      // Blockquote styles for pasted content
      '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:my-2',
      // Inline image styles for pasted images
      '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-1',
    );
  }, []);

  const labelClasses = useMemo(() => {
    const isFloating = hasValue || isFocused;
    return classNames(
      'absolute left-0 ml-3 pointer-events-none transition-all duration-100 ease-in-out origin-top-left paragraph-2',
      {
        'text-red-500 dark:text-red-400': isValid === false,
        'text-green-500 dark:text-green-400': isValid === true,
        'text-gray-500 dark:text-gray-400': isValid === undefined || isValid === null,
      },
      isFloating ?
        '-top-2 scale-75 backdrop-blur-lg bg-white/30 dark:bg-gray-800/30 px-1 rounded' :
        'top-3 scale-100',
    );
  }, [hasValue, isFocused, isValid]);

  const validationMessageClasses = 'paragraph-2 text-red-500 dark:text-red-400';

  return {
    containerClasses,
    editorWrapperClasses,
    textareaClasses,
    labelClasses,
    validationMessageClasses,
  };
};
